import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtDate, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner } from "@/components/shared";
import { RefreshCw, Plug, Unplug, PackagePlus } from "lucide-react";
import { toast } from "sonner";

const META = {
  shopify: { name: "Shopify", color: "bg-[#95bf47]", desc: "Orders, refunds and products sync automatically through the ConsignOut Shopify app." },
  woocommerce: { name: "WooCommerce", color: "bg-[#7f54b3]", desc: "Approve read-only access in your WooCommerce admin. ConsignOut never sees your WordPress password." },
};

const Stat = ({ label, value, testid }) => (
  <div><p className="text-xs text-slate-400">{label}</p><p className="font-medium text-slate-700 truncate" data-testid={testid}>{value}</p></div>
);

function ConnectedActions({ i, entitled, run, onDisconnect }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3 text-sm bg-slate-50 rounded-lg p-3">
        <Stat label="Store" value={i.shop || i.store_name || "—"} testid={`integration-store-${i.platform}`} />
        <Stat label="Last sync" value={i.last_sync ? fmtDate(i.last_sync) : "Never"} testid={`integration-last-sync-${i.platform}`} />
        <Stat label="Orders" value={i.records_imported || 0} testid={`integration-records-${i.platform}`} />
      </div>
      {i.platform === "shopify" && (
        i.webhooks?.active
          ? <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="integration-webhooks-shopify">Live updates on: new orders, edits, cancellations and refunds arrive automatically.</p>
          : <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="integration-webhooks-shopify">
              {i.webhooks?.errors?.length ? `Live updates need attention: ${i.webhooks.errors[0]}` : "Live updates are being set up. Reopen ConsignOut in a minute to check."}
            </p>
      )}
      {!entitled && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`integration-subscription-${i.platform}`}>Syncing needs an active subscription. <Link to="/billing" className="underline font-semibold">Go to Billing</Link></p>}
      <div className="flex flex-wrap gap-2">
        <button data-testid={`sync-${i.platform}`} disabled={!entitled} onClick={() => run(i.platform, "sync")} className="flex-1 flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-semibold py-2 rounded-lg text-sm"><RefreshCw className="w-4 h-4" /> Sync orders</button>
        <button data-testid={`import-products-${i.platform}`} disabled={!entitled} onClick={() => run(i.platform, "import-products")} className="flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-100 disabled:opacity-50 text-slate-700 font-semibold py-2 px-3 rounded-lg text-sm"><PackagePlus className="w-4 h-4" /> Import products</button>
        {onDisconnect && <button data-testid={`disconnect-${i.platform}`} onClick={onDisconnect} className="flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-100 text-slate-600 font-semibold py-2 px-3 rounded-lg text-sm"><Unplug className="w-4 h-4" /> Disconnect</button>}
      </div>
      {i.platform === "shopify" && <p className="text-xs text-slate-400">New orders and refunds also arrive instantly via Shopify webhooks. To disconnect, uninstall ConsignOut in Shopify admin → Settings → Apps.</p>}
    </div>
  );
}

function WooConnect() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/woocommerce/authorize", { store_url: url, origin_url: window.location.origin });
      window.location.href = data.authorize_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <input data-testid="woo-store-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yourstore.com" className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500 w-full" />
      <button data-testid="connect-woocommerce" disabled={busy || !url.trim()} onClick={go} className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm"><Plug className="w-4 h-4" /> Connect WooCommerce</button>
      <p className="text-xs text-slate-400">You'll be sent to your WooCommerce admin to approve read-only access, then returned here.</p>
    </div>
  );
}

const ShopifyNotConnected = () => (
  <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3" data-testid="shopify-install-help">Install ConsignOut from the Shopify App Store and open it from your Shopify admin. Shopify connects this store automatically. You never paste tokens or passwords.</p>
);

export default function Integrations() {
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = () => api.get("/integrations").then((r) => setData(r.data));
  useEffect(() => {
    if (params.get("woo")) {
      if (params.get("success") === "0") toast.error("WooCommerce access was not approved");
      else toast.success("WooCommerce approval received");
      setParams({});
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (platform, action) => {
    toast.loading(action === "sync" ? "Syncing orders…" : "Importing products…", { id: "run" });
    try {
      const { data: r } = await api.post(`/integrations/${platform}/${action}`);
      toast[r.ok ? "success" : "error"](r.message, { id: "run" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: "run" }); }
  };
  const disconnectWoo = async () => {
    const { data: r } = await api.post("/integrations/woocommerce/disconnect");
    toast.success(r.message); load();
  };

  if (!data) return <Spinner />;
  return (
    <div>
      <PageHeader title="Integrations" subtitle="Bring orders, refunds and products in from your store" />
      <div className="grid md:grid-cols-2 gap-5">
        {data.integrations.map((i) => {
          const m = META[i.platform];
          const connected = i.status === "connected";
          return (
            <div key={i.platform} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid={`integration-${i.platform}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-11 h-11 rounded-xl ${m.color} flex items-center justify-center text-white font-bold font-display`}>{m.name.charAt(0)}</div>
                <div><p className="font-display font-bold text-slate-900">{m.name}</p><StatusBadge status={i.status} /></div>
              </div>
              <p className="text-sm text-slate-500 mb-4">{m.desc}</p>
              {(i.last_error || i.reauth_required) && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid={`integration-msg-${i.platform}`}>{i.reauth_required ? "Shopify authorization expired. Open ConsignOut from your Shopify admin to reconnect." : i.last_error}</p>
              )}
              {connected
                ? <ConnectedActions i={i} entitled={data.entitled} run={run} onDisconnect={i.platform === "woocommerce" ? disconnectWoo : null} />
                : i.platform === "shopify" ? <ShopifyNotConnected /> : <WooConnect />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
